import { concat, find, size, some } from 'es-toolkit/compat';
import {
  assignManifestDraftCaseIds,
  ManifestCaseTarget,
  ManifestDialogUrlState,
  ManifestDraftCase,
} from './dialogState';
import { applyManifestDraftPatches, removeSlidesFromManifestCases } from './manifestResolution';

export const caseNameFromFileName = (fileName?: string | null, fallback = 'Unnamed file') => {
  const withoutExtension = (fileName || '').replace(/\.[^./\\]+$/, '');
  const stripped = withoutExtension.replace(/\.ome$/i, '').trim();
  return stripped || fallback;
};

export const updateManifestCaseTarget = ({
  previousState,
  currentDraftCases,
  draftCaseId,
  slideId,
  previousTarget,
  target,
  useFileNameForCaseLabel = false,
}: {
  previousState: ManifestDialogUrlState;
  currentDraftCases: ManifestDraftCase[];
  draftCaseId?: number;
  slideId: string;
  previousTarget: ManifestCaseTarget;
  target: ManifestCaseTarget;
  useFileNameForCaseLabel?: boolean;
}): ManifestDialogUrlState => {
  const containsSlide = (procedure: ManifestDraftCase) => some(procedure.slides, { id: slideId });
  const rawCase =
    find(previousState.draftCases, (procedure) => procedure.id === draftCaseId && containsSlide(procedure)) ??
    find(previousState.draftCases, containsSlide);
  const hasOtherDraftRow =
    draftCaseId !== undefined &&
    previousState.draftCases?.some((procedure) => procedure !== rawCase && containsSlide(procedure));
  const nextState = {
    ...previousState,
    draftCases: previousState.draftCases?.map((procedure) =>
      containsSlide(procedure) && size(procedure.slides) === 1 && (draftCaseId === undefined || procedure === rawCase)
        ? {
            ...procedure,
            draftTarget: target,
            draftIssues: procedure.draftIssues?.filter((issue) => issue.field !== 'targetCaseAssignment'),
          }
        : procedure
    ),
    existingCaseAssignmentsBySlideId: hasOtherDraftRow
      ? previousState.existingCaseAssignmentsBySlideId
      : { ...previousState.existingCaseAssignmentsBySlideId, [slideId]: target },
  };
  if (target !== 'new' && (!rawCase || size(rawCase.slides) === 1)) return nextState;
  const displayedCase =
    target !== 'new' && rawCase
      ? applyManifestDraftPatches(
          [rawCase],
          previousState.casePatchesById || {},
          previousState.slidePatchesById || {}
        )[0]
      : find(currentDraftCases, (procedure) => procedure.id === draftCaseId && containsSlide(procedure)) ??
        find(currentDraftCases, containsSlide);
  const slide = find(displayedCase?.slides, { id: slideId });
  if (!displayedCase || !slide) return nextState;
  const fileNameLabel = useFileNameForCaseLabel ? caseNameFromFileName(slide.originalFileName, slide.id) : undefined;
  if (target === 'new' && previousTarget === 'new' && size(displayedCase.slides) === 1) {
    if (fileNameLabel === undefined) return nextState;
    return {
      ...nextState,
      casePatchesById: {
        ...previousState.casePatchesById,
        [displayedCase.id]: { ...previousState.casePatchesById?.[displayedCase.id], label: fileNameLabel },
      },
    };
  }
  const [detachedCase] = assignManifestDraftCaseIds({
    cases: [
      {
        ...displayedCase,
        label:
          target !== 'new'
            ? displayedCase.label
            : fileNameLabel ??
              (displayedCase.draftIssues?.some((issue) => issue.field === 'targetCaseAssignment')
                ? displayedCase.label
                : ''),
        slides: [slide],
        ...((target !== 'new' || useFileNameForCaseLabel || displayedCase.draftTarget !== undefined) && {
          draftTarget: target,
        }),
        ...(displayedCase.draftIssues && {
          draftIssues: displayedCase.draftIssues.filter((issue) => issue.field !== 'targetCaseAssignment'),
        }),
      },
    ],
    retainedCases: concat(previousState.draftCases || [], currentDraftCases),
    casePatchesById: previousState.casePatchesById,
  });
  return {
    ...nextState,
    draftCases: concat(
      (nextState.draftCases || []).flatMap((procedure) =>
        draftCaseId === undefined || procedure.id === rawCase?.id
          ? removeSlidesFromManifestCases([procedure], [slideId])
          : [procedure]
      ),
      detachedCase
    ),
  };
};
